// ReturnRequestManagement.jsx (Fixed version)

import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import ApiURL from "../Api/Api";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarContext } from "./SidebarContext";
import TransactionImageField from "./TransactionImageField";
import { fetchTransactionPhotoRequired, imagePayload } from "../utils/transactionPhoto";
import { formatKolkataDateTime } from "../utils/dateFormat";

const ReturnRequestManagement = () => {
  const [returnRequests, setReturnRequests] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [netBalance, setNetBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [partialAmounts, setPartialAmounts] = useState({});
  const [purpose, setPurpose] = useState("");
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [transactionImage, setTransactionImage] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const user_id = localStorage.getItem("id");
  const role = localStorage.getItem("role");
  const requesterLabel = role === "admin" || role === "company_owner" ? "Admin" : "Worker";
  const reportTitle =
    role === "company_owner"
      ? "Admin Return Request Report"
      : role === "admin"
      ? "Return To Company Owner"
      : "Return Request Management";
  const submitReturnTitle = role === "admin" ? "Submit Return To Company Owner" : "Submit Return Request";

  const urlParams = new URLSearchParams(location.search);
  const highlightTransactionId = urlParams.get("highlight");

  const fetchReturnRequests = async () => {
    if (!["worker", "admin", "company_owner"].includes(role) || !user_id) {
      setError("Access denied");
      toast.error("Access denied");
      return;
    }

    setIsLoading(true);
    try {
      const url = new URL(`${ApiURL}/getReturnRequests.php`);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("role", role);
      console.log('Fetching return requests URL:', url.toString());

      const response = await fetch(url.toString());
      
      // Check if response is OK before trying to parse JSON
      if (!response.ok) {
        const textResponse = await response.text();
        console.error('Server response:', textResponse);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      console.log('Fetched return requests:', JSON.stringify(data, null, 2));

      if (data.status === "success") {
        const sortedRequests = (data.requests || []).map(req => ({
          ...req,
          total_return_amount: parseFloat(req.total_return_amount) || 0,
          transaction_count: parseInt(req.transaction_count) || 0,
          transactions: (req.transactions || []).map(txn => ({
            ...txn,
            return_amount: parseFloat(txn.return_amount) || 0,
            transaction_no: parseInt(txn.transaction_no) || 0,
            transaction_created_date: txn.transaction_created_date || req.created_at,
            transaction_created_by_name: txn.transaction_created_by_name || req.created_by_name || "-"
          }))
        })).sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          if (dateA - dateB !== 0) return dateB - dateA;
          return b.id - a.id;
        });
        setReturnRequests(sortedRequests);
        setError(null);
      } else {
        throw new Error(data.message || "Failed to load return requests");
      }
    } catch (err) {
      console.error('Fetch return requests error:', err);
      setError("Failed to fetch return requests: " + err.message);
      toast.error("Failed to fetch return requests: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async () => {
    if (!["worker", "admin"].includes(role)) return;
    try {
      const url = new URL(`${ApiURL}/getWorkerTransaction.php`);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("role", role);
      url.searchParams.append("amount_type", "credit");

      const response = await fetch(url.toString());
      
      if (!response.ok) {
        const textResponse = await response.text();
        console.error('Server response:', textResponse);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      console.log('Fetched transactions:', JSON.stringify(data, null, 2));

      if (data.status === "success") {
        const sortedTransactions = (data.transactions || []).sort((a, b) => {
          const dateA = new Date(a.transaction_date);
          const dateB = new Date(b.transaction_date);
          if (dateA - dateB !== 0) return dateA - dateB;
          return new Date(a.created_date) - new Date(b.created_date);
        });
        let runningBalance = 0;
        const transactionsWithCumulative = sortedTransactions.map((txn, index) => {
          runningBalance += parseFloat(txn.credit_amount || 0) - parseFloat(txn.debit_amount || 0);
          return {
            ...txn,
            cumulative_balance: runningBalance,
            serial_no: index + 1,
            is_returned: false
          };
        });
        setTransactions(transactionsWithCumulative);
        setNetBalance(parseFloat(data.net_balance) || 0);
        if (highlightTransactionId) {
          const highlighted = transactionsWithCumulative.find(
            (txn) => String(txn.transaction_no) === String(highlightTransactionId)
          );
          if (highlighted && !highlighted.is_returned) {
            setSelectedTransactions([highlighted]);
            setPartialAmounts({ [highlighted.transaction_no]: parseFloat(highlighted.credit_amount) || 0 });
          } else {
            toast.error(highlighted ? "Highlighted transaction already returned." : "Highlighted transaction not found.");
          }
        }
      } else {
        throw new Error(data.message || "Failed to load transactions");
      }
    } catch (err) {
      console.error('Fetch transactions error:', err);
      toast.error("Failed to fetch transactions: " + err.message);
    }
  };

  useEffect(() => {
    if (transactions.length === 0 || returnRequests.length === 0) return;

    const returnedTransactionNos = new Set();
    returnRequests.forEach(req => {
      if (["approved", "pending"].includes((req.status || "").toLowerCase()) && Number(req.worker_id) === Number(user_id)) {
        (req.transactions || []).forEach(txn => {
          returnedTransactionNos.add(parseInt(txn.transaction_no));
        });
      }
    });

    const updatedTransactions = transactions.map(txn => ({
      ...txn,
      is_returned: returnedTransactionNos.has(parseInt(txn.transaction_no))
    }));

    setTransactions(updatedTransactions);
    console.log('Updated transactions with is_returned:', JSON.stringify(updatedTransactions, null, 2));

    setSelectedTransactions(prev => prev.filter(txn => !returnedTransactionNos.has(parseInt(txn.transaction_no))));
  }, [returnRequests, transactions]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const creditTransactions = transactions
        .filter((txn) => txn.amount_type === "credit" && !txn.is_returned)
        .filter((txn, index, rows) =>
          rows.findIndex((row) => String(row.transaction_no) === String(txn.transaction_no)) === index
        );
      setSelectedTransactions(creditTransactions);
      const newPartialAmounts = {};
      creditTransactions.forEach((txn) => {
        newPartialAmounts[txn.transaction_no] = parseFloat(txn.credit_amount) || 0;
      });
      setPartialAmounts(newPartialAmounts);
    } else {
      setSelectedTransactions([]);
      setPartialAmounts({});
    }
  };

  const handleTransactionSelect = (txn) => {
    if (txn.is_returned) {
      toast.warn("This transaction has already been returned and cannot be selected.");
      return;
    }
    if (txn.amount_type !== "credit") return;
    if (selectedTransactions.some((t) => t.transaction_no === txn.transaction_no)) {
      setSelectedTransactions(selectedTransactions.filter((t) => t.transaction_no !== txn.transaction_no));
      setPartialAmounts((prev) => {
        const newAmounts = { ...prev };
        delete newAmounts[txn.transaction_no];
        return newAmounts;
      });
    } else {
      setSelectedTransactions([...selectedTransactions, txn]);
      setPartialAmounts((prev) => ({
        ...prev,
        [txn.transaction_no]: parseFloat(txn.credit_amount) || 0,
      }));
    }
  };

  const handlePartialAmountChange = (transaction_no, value) => {
    const amount = parseFloat(value) || 0;
    const maxAmount = parseFloat(transactions.find((txn) => txn.transaction_no === transaction_no)?.credit_amount) || 0;
    if (amount < 0 || amount > maxAmount) {
      toast.error(`Amount must be between 0 and ${formatCurrency(maxAmount)}`);
      return;
    }
    setPartialAmounts((prev) => ({
      ...prev,
      [transaction_no]: amount,
    }));
  };

  const submitReturnRequest = async () => {
    if (!selectedTransactions.length) {
      toast.error("Please select at least one transaction");
      return;
    }
    if (!purpose.trim()) {
      toast.error("Purpose is required");
      return;
    }

    const uniqueSelectedTransactions = selectedTransactions.filter((txn, index, rows) =>
      rows.findIndex((row) => String(row.transaction_no) === String(txn.transaction_no)) === index
    );

    const total_amount = uniqueSelectedTransactions.reduce(
      (sum, txn) => sum + (parseFloat(partialAmounts[txn.transaction_no]) || 0),
      0
    );
    if (total_amount <= 0) {
      toast.error("Total return amount must be greater than zero");
      return;
    }
    if (total_amount > netBalance) {
      toast.error(`Cannot return amount above your net balance (${formatCurrency(netBalance)})`);
      return;
    }
    if (photoRequired && !transactionImage?.dataUrl) {
      toast.error("Transaction photo is required by company settings");
      return;
    }

    const payload = {
      worker_id: parseInt(user_id),
      role,
      transactions: uniqueSelectedTransactions.map((txn) => ({
        transaction_no: parseInt(txn.transaction_no),
        amount: parseFloat(partialAmounts[txn.transaction_no] || txn.credit_amount),
        purpose: purpose.trim(),
      })),
      total_amount,
      return_type: uniqueSelectedTransactions.length > 1 ? "multiple" : "specific",
      attachment_image: imagePayload(transactionImage),
    };

    try {
      const response = await fetch(`${ApiURL}/submitReturnRequest.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error response:', errorText);
        throw new Error(`Server error (${response.status}): ${errorText.substring(0, 200)}`);
      }

      const data = await response.json();
      if (data.status === "success") {
        toast.success("Return request submitted successfully");
        setShowReturnModal(false);
        setSelectedTransactions([]);
        setPartialAmounts({});
        setPurpose("");
        setTransactionImage(null);
        await fetchReturnRequests();
        await fetchTransactions(); // Refresh transactions
      } else {
        throw new Error(data.message || "Failed to submit return request");
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error("Failed to submit return request: " + err.message);
    }
  };

  const handleAction = async (requestId, action, reason = "") => {
    setShowConfirmModal({ requestId, action, reason });
  };

  const confirmAction = async () => {
  const { requestId, action, reason } = showConfirmModal;

  try {
    const response = await fetch(`${ApiURL}/manageReturnRequest.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        request_id: parseInt(requestId),
        action,
        rejection_reason: reason,
        admin_id: parseInt(user_id),
      }),
    });

    // ✅ ONLY READ ONCE
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `Server error (${response.status})`);
    }

    if (data.status === "success") {
      toast.success(`Request ${action}d successfully`);
      await fetchReturnRequests();
      await fetchTransactions();
      setSelectedRequest(null);
      setRejectionReason("");
      setShowConfirmModal(null);
      setExpandedRequest(null);
    } else {
      throw new Error(data.message || `Failed to ${action} request`);
    }

  } catch (err) {
    console.error('Action error:', err);
    toast.error(`Failed to ${action} request: ` + err.message);
  }
};

  const toggleDetails = (requestId) => {
    console.log('Toggling details for request:', requestId);
    setExpandedRequest(expandedRequest === requestId ? null : requestId);
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchReturnRequests();
      if (["worker", "admin"].includes(role)) await fetchTransactions();
      if (highlightTransactionId && !/^\d+$/.test(highlightTransactionId)) {
        toast.error("Invalid transaction ID in URL.");
      }
      fetchTransactionPhotoRequired(user_id).then(setPhotoRequired).catch(() => {});
    };
    loadData();
  }, [role, highlightTransactionId]);

  const formatCurrency = (value) => {
    return value != null ? `₹${Number(value).toLocaleString("en-IN")}` : "₹0";
  };

  const formatDate = (dateStr) => {
    return formatKolkataDateTime(dateStr, { dateOnly: true });
  };

  const formatDateTime = (dateStr) => {
    return formatKolkataDateTime(dateStr);
  };

  return (
    <div className="min-h-screen bg-[#f0f2f8] font-sans">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`min-w-0 flex-1 min-h-screen transition-all duration-300 ${
            isSidebarOpen ? "lg:ml-72" : ""
          } ${isSidebarOpen && screenWidth < 640 ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">
          <div className="mb-4">
            <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">{reportTitle}</h1>
            <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">Select credit transactions and submit a return request.</p>
          </div>

          {["worker", "admin"].includes(role) && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-4 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden p-4">
              <h2 className="text-sm font-bold text-slate-800 mb-4">
                {role === "admin" ? "Available Credit Transactions To Return To Company Owner" : "Available Transactions for Return"}
              </h2>
              <div className="overflow-x-auto relative">
                <table className="min-w-[1100px] w-full text-[12px] text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/80">
                      <th className="px-1 py-1.5 w-12">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            selectedTransactions.length === transactions.filter((txn) => txn.amount_type === "credit" && !txn.is_returned).length &&
                            transactions.length > 0
                          }
                          disabled={transactions.filter((txn) => txn.amount_type === "credit" && !txn.is_returned).length === 0}
                        />
                      </th>
                      <th className="px-1 py-1.5 w-12">Serial No</th>
                      <th className="px-1 py-1.5 w-16">Date</th>
                      <th className="px-1 py-1.5 w-16">Type</th>
                      <th className="px-1 py-1.5 w-16">Amount</th>
                      <th className="px-1 py-1.5 w-16">Return Amount</th>
                      <th className="px-1 py-1.5 w-24">Particulars</th>
                      <th className="px-1 py-1.5 w-16">Site</th>
                      <th className="px-1 py-1.5 w-20">Main Head</th>
                      <th className="px-1 py-1.5 w-20">Sub Head</th>
                      <th className="px-1 py-1.5 w-24">Remarks</th>
                      <th className="px-1 py-1.5 w-20">Created By</th>
                      <th className="px-1 py-1.5 w-28">Created At</th>
                      <th className="px-1 py-1.5 w-20">Updated By</th>
                      <th className="px-1 py-1.5 w-28">Updated At</th>
                      <th className="px-1 py-1.5 w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((txn, index) => (
                      <motion.tr
                        key={txn.transaction_no || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`border-b hover:bg-gray-100 ${
                          highlightTransactionId && String(txn.transaction_no) === String(highlightTransactionId) 
                            ? "bg-yellow-100" 
                            : txn.is_returned 
                              ? "bg-gray-300 opacity-70 line-through" 
                              : "bg-white"
                        }`}
                      >
                        <td className="px-1 py-1.5">
                          {txn.is_returned ? (
                            <button
                              className="px-2 py-1 bg-gray-400 text-white rounded-lg text-xs cursor-not-allowed"
                              disabled
                            >
                              Already Returned
                            </button>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedTransactions.some((t) => t.transaction_no === txn.transaction_no)}
                              onChange={() => handleTransactionSelect(txn)}
                              disabled={txn.amount_type !== "credit" || txn.is_returned}
                              className="focus:ring-2 focus:ring-indigo-500"
                            />
                          )}
                        </td>
                        <td className="px-1 py-1.5">{txn.serial_no}</td>
                        <td className="px-1 py-1.5">{formatDate(txn.transaction_date)}</td>
                        <td className="px-1 py-1.5">
                          <span
                            className={`px-1 py-0.5 rounded-full text-[10px] ${
                              txn.amount_type === "credit" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            } ${txn.is_returned ? "opacity-50" : ""}`}
                          >
                            {txn.amount_type}
                          </span>
                        </td>
                        <td className="px-1 py-1.5">{formatCurrency(txn.credit_amount)}</td>
                        <td className="px-1 py-1.5">
                          {txn.amount_type === "credit" && selectedTransactions.some((t) => t.transaction_no === txn.transaction_no) && !txn.is_returned ? (
                            <input
                              type="number"
                              value={partialAmounts[txn.transaction_no] || ""}
                              onChange={(e) => handlePartialAmountChange(txn.transaction_no, e.target.value)}
                              className="w-full p-1 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500"
                              min="0"
                              step="0.01"
                              placeholder="Enter return amount"
                              disabled={txn.is_returned}
                            />
                          ) : (
                            formatCurrency(txn.return_amount || 0)
                          )}
                        </td>
                        <td className="px-1 py-1.5">{txn.particuler_to || "-"}</td>
                        <td className="px-1 py-1.5">{txn.site || "-"}</td>
                        <td className="px-1 py-1.5">{txn.main_head || "-"}</td>
                        <td className="px-1 py-1.5">{txn.sub_head || "-"}</td>
                        <td className="px-1 py-1.5">{txn.remarks || "-"}</td>
                        <td className="px-1 py-1.5">{txn.created_by || "-"}</td>
                        <td className="px-1 py-1.5">{formatDate(txn.created_date)}</td>
                        <td className="px-1 py-1.5">{txn.updated_by || "-"}</td>
                        <td className="px-1 py-1.5">{txn.updated_date ? formatDate(txn.updated_date) : "-"}</td>
                        <td className="px-1 py-1.5">
                          {txn.is_returned ? (
                            <span className="px-1 py-0.5 rounded-full text-[10px] bg-gray-200 text-gray-700">
                              Returned
                            </span>
                          ) : (
                            <span className="px-1 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700">
                              Available
                            </span>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <p className="text-sm font-semibold">Net Balance: {formatCurrency(netBalance)}</p>
                <button
                  onClick={() => setShowReturnModal(true)}
                  className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                  disabled={selectedTransactions.length === 0}
                >
                  {role === "admin" ? "Submit Return to Company" : "Submit Return Request"}
                </button>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-3 text-red-500 text-xs sm:text-sm"
            >
              {error}
            </motion.div>
          )}

          {isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-6 text-gray-500 animate-pulse text-xs sm:text-sm"
            >
              Loading return requests...
            </motion.div>
          ) : returnRequests.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-6 text-gray-500 text-xs sm:text-sm"
            >
              No return requests found
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="overflow-x-auto shadow-md rounded-md max-w-[100vw] max-h-[400px] relative"
            >
              <table className="min-w-[900px] w-full text-xs text-left text-gray-700 border-collapse">
                <thead className="text-xs text-white uppercase bg-gradient-to-r from-indigo-500 to-blue-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-1 py-1.5 w-12">Request ID</th>
                    <th className="px-1 py-1.5 w-16">{requesterLabel}</th>
                    <th className="px-1 py-1.5 w-16">Date</th>
                    <th className="px-1 py-1.5 w-16">Type</th>
                    <th className="px-1 py-1.5 w-16">Amount</th>
                    <th className="px-1 py-1.5 w-24">Particulars</th>
                    <th className="px-1 py-1.5 w-16">Site</th>
                    <th className="px-1 py-1.5 w-20">Main Head</th>
                    <th className="px-1 py-1.5 w-20">Sub Head</th>
                    <th className="px-1 py-1.5 w-24">Purpose</th>
                    <th className="px-1 py-1.5 w-16">Status</th>
                    <th className="px-1 py-1.5 w-24">Rejection Reason</th>
                    <th className="px-1 py-1.5 w-20">Created By</th>
                    <th className="px-1 py-1.5 w-28">Created At</th>
                    <th className="px-1 py-1.5 w-20">Updated By</th>
                    <th className="px-1 py-1.5 w-28">Updated At</th>
                    <th className="px-1 py-1.5 w-16">Return Type</th>
                    {["admin", "company_owner"].includes(role) && <th className="px-1 py-1.5 w-20">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {returnRequests.map((req) => (
                    <React.Fragment key={req.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className={`border-b hover:bg-gray-100 ${
                          req.status === "pending"
                            ? "bg-yellow-50"
                            : req.status === "approved"
                            ? "bg-green-50"
                            : "bg-red-50"
                        }`}
                      >
                        <td className="px-1 py-1.5">{req.id}</td>
                        <td className="px-1 py-1.5">{req.requester_name || req.worker_name || "-"}</td>
                        <td className="px-1 py-1.5">{req.transactions[0]?.transaction_date ? formatDate(req.transactions[0].transaction_date) : "-"}</td>
                        <td className="px-1 py-1.5">
                          <span
                            className={`px-1 py-0.5 rounded-full text-[10px] ${
                              req.transactions[0]?.amount_type === "credit"
                                ? "bg-green-100 text-green-700"
                                : req.transactions[0]?.amount_type === "debit"
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {req.transactions[0]?.amount_type || "-"}
                          </span>
                        </td>
                        <td className="px-1 py-1.5">{formatCurrency(req.total_return_amount)}</td>
                        <td className="px-1 py-1.5">
                          {req.transaction_count > 1 ? (
                            <button
                              onClick={() => toggleDetails(req.id)}
                              className="text-blue-500 hover:underline text-xs"
                            >
                              View {req.transaction_count} Transactions
                            </button>
                          ) : (
                            req.transactions[0]?.particuler_to || "-"
                          )}
                        </td>
                        <td className="px-1 py-1.5">{req.transactions[0]?.site || "-"}</td>
                        <td className="px-1 py-1.5">{req.transactions[0]?.main_head || "-"}</td>
                        <td className="px-1 py-1.5">{req.transactions[0]?.sub_head || "-"}</td>
                        <td className="px-1 py-1.5">{req.purpose || "-"}</td>
                        <td className="px-1 py-1.5">
                          <span
                            className={`px-1 py-0.5 rounded-full text-[10px] ${
                              req.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : req.status === "approved"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {req.status || "-"}
                          </span>
                        </td>
                        <td className="px-1 py-1.5">{req.rejection_reason || "-"}</td>
                        <td className="px-1 py-1.5">{req.created_by_name || "-"}</td>
                        <td className="px-1 py-1.5">{formatDateTime(req.created_at)}</td>
                        <td className="px-1 py-1.5">{req.updated_by_name || "-"}</td>
                        <td className="px-1 py-1.5">{req.updated_at ? formatDateTime(req.updated_at) : "-"}</td>
                        <td className="px-1 py-1.5">{req.return_type || "-"}</td>
                        {["admin", "company_owner"].includes(role) && req.status === "pending" && req.approval_role === "approver" && (
                          <td className="px-1 py-1.5">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAction(req.id, "approve")}
                                className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => setSelectedRequest(req.id)}
                                className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                      {req.transaction_count > 1 && expandedRequest === req.id && (
                        <motion.tr
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          transition={{ duration: 0.3 }}
                          className="bg-gray-50"
                        >
                          <td colSpan={["admin", "company_owner"].includes(role) ? 18 : 17} className="px-1 py-1.5">
                            <div className="pl-4">
                              <h4 className="text-xs font-semibold mb-2">Transaction Details</h4>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-gray-200">
                                    <th className="px-1 py-1">Transaction No</th>
                                    <th className="px-1 py-1">Amount</th>
                                    <th className="px-1 py-1">Date</th>
                                    <th className="px-1 py-1">Particulars</th>
                                    <th className="px-1 py-1">Site</th>
                                    <th className="px-1 py-1">Main Head</th>
                                    <th className="px-1 py-1">Sub Head</th>
                                    <th className="px-1 py-1">Created At</th>
                                    <th className="px-1 py-1">Created By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(req.transactions || []).map((txn) => (
                                    <tr key={txn.transaction_no} className="border-b">
                                      <td className="px-1 py-1">{txn.transaction_no || "-"}</td>
                                      <td className="px-1 py-1">{formatCurrency(txn.return_amount)}</td>
                                      <td className="px-1 py-1">{formatDate(txn.transaction_date)}</td>
                                      <td className="px-1 py-1">{txn.particuler_to || "-"}</td>
                                      <td className="px-1 py-1">{txn.site || "-"}</td>
                                      <td className="px-1 py-1">{txn.main_head || "-"}</td>
                                      <td className="px-1 py-1">{txn.sub_head || "-"}</td>
                                      <td className="px-1 py-1">{formatDateTime(txn.transaction_created_date)}</td>
                                      <td className="px-1 py-1">{txn.transaction_created_by_name}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </motion.div>
          )}

          {/* Modals remain the same */}
          {showReturnModal && ["worker", "admin"].includes(role) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <h2 className="text-lg font-semibold mb-4">{submitReturnTitle}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selected Transactions</label>
                    {selectedTransactions.map((txn) => (
                      <div key={txn.transaction_no} className="text-sm">
                        <p>Transaction #{txn.transaction_no} - {formatCurrency(txn.credit_amount)}</p>
                        <p>Return Amount: {formatCurrency(partialAmounts[txn.transaction_no] || 0)}</p>
                        <p>Date: {formatDate(txn.transaction_date)}</p>
                        <p>Type: {txn.amount_type}</p>
                        <p>Particulars: {txn.particuler_to || "-"}</p>
                        <p>Site: {txn.site || "-"}</p>
                        <p>Main Head: {txn.main_head || "-"}</p>
                        <p>Sub Head: {txn.sub_head || "-"}</p>
                        <p>Created By: {txn.created_by || "-"}</p>
                        <p>Created At: {formatDate(txn.created_date)}</p>
                        <hr className="my-2" />
                      </div>
                    ))}
                    <p className="font-semibold">
                      Total: {formatCurrency(selectedTransactions.reduce((sum, txn) => sum + (parseFloat(partialAmounts[txn.transaction_no]) || 0), 0))}
                    </p>
                    <p className="font-semibold text-gray-700">
                      Net Balance: {formatCurrency(netBalance)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                    <textarea
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter purpose for return"
                      required
                    />
                  </div>
                  <TransactionImageField
                    value={transactionImage}
                    onChange={setTransactionImage}
                    required={photoRequired}
                  />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setShowReturnModal(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitReturnRequest}
                    className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm"
                  >
                    {role === "admin" ? "Submit Return To Owner" : "Submit Return"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {selectedRequest && ["admin", "company_owner"].includes(role) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <h2 className="text-lg font-semibold mb-4">Reject Return Request</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full p-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter reason for rejection"
                      required
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAction(selectedRequest, "reject", rejectionReason)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                    disabled={!rejectionReason.trim()}
                  >
                    Reject
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showConfirmModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full"
              >
                <h2 className="text-lg font-semibold mb-4">
                  Confirm {showConfirmModal.action === "approve" ? "Approval" : "Rejection"}
                </h2>
                <p className="text-sm mb-4">
                  Are you sure you want to {showConfirmModal.action} this return request?
                  {showConfirmModal.action === "reject" && (
                    <span> Reason: {showConfirmModal.reason}</span>
                  )}
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowConfirmModal(null)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-white rounded-lg text-sm ${
                      showConfirmModal.action === "approve"
                        ? "bg-green-500 hover:bg-green-600"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
          </div>
        </motion.main>
        {isSidebarOpen && screenWidth < 640 && (
          <div
            className="fixed inset-0 bg-black/50 z-30"
            onClick={() => document.querySelector('button[aria-label="Close Sidebar"]')?.click()}
            aria-hidden="true"
          />
        )}
      </div>

      <style jsx>{`
        .rupee::before {
          content: "₹";
          font-size: 0.9em;
          margin-right: 2px;
        }
        .overflow-x-auto {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        .max-h-[400px] {
          max-height: 400px;
          overflow-y: auto;
        }
        .sticky {
          position: sticky;
          top: 0;
          z-index: 10;
        }
        @media (max-width: 640px) {
          .overflow-x-auto {
            max-width: 100vw;
          }
          table {
            min-width: 900px;
            width: auto;
          }
          .border-gradient-to-r {
            border-image: linear-gradient(to right, #4f46e5, #3b82f6) 1;
            padding: 0.25rem;
          }
          input[type="date"], input[type="number"], textarea {
            font-size: 0.75rem;
            padding: 0.5rem;
          }
        }
        @media (min-width: 640px) {
          .border-gradient-to-r {
            border-image: linear-gradient(to right, #4f46e5, #3b82f6) 1;
          }
          input[type="date"], input[type="number"], textarea {
            font-size: 0.875rem;
            padding: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ReturnRequestManagement;
